// UserProfile.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    FaLinkedin,
    FaFacebook,
    FaInstagram,
    FaTiktok,
    FaTwitter,
    FaGlobe,
    FaBuilding,
    FaFileDownload,
    FaVideo,
    FaUserPlus,
    FaEllipsisV
} from 'react-icons/fa';
import { BsPlayFill, BsEye, BsSortDown } from 'react-icons/bs';
import { IoClose } from 'react-icons/io5';
import api from '../../utils/axios';
import Loading from '../../components/Loading'; // Fix the import path
import './UserProfile.css';
import axios from 'axios';
import CommentItem from '../../components/CommentItem'; // Add this import at the top

const UserProfile = () => {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [connectionsCount, setConnectionsCount] = useState(0);
    const [showMenu, setShowMenu] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const menuRef = useRef(null);
    const [posts, setPosts] = useState([]);
    const [sortBy, setSortBy] = useState('recent');
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [hasIncrementedView, setHasIncrementedView] = useState(false); // Add this state
    const videoRef = useRef(null); // Add this ref

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        if (days < 30) return `${days}d ago`;
        const months = Math.floor(days / 30);
        if (months < 12) return `${months}mo ago`;
        return `${Math.floor(months / 12)}y ago`;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.addEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [
                    userResponse,
                    currentUserResponse,
                    connectionResponse,
                    connectionsCountResponse,
                ] = await Promise.all([
                    api.get(`/user/${id}/profile`),
                    api.get('/user/current'),
                    api.get(`/user/connections/status/${id}`),
                    api.get(`/user/${id}/connections/count`),
                ]);

                setUser(userResponse.data);
                setCurrentUserId(currentUserResponse.data.id);
                setConnectionStatus(connectionResponse.data.status);
                setConnectionsCount(connectionsCountResponse.data.count);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to fetch user data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();

        // Add connection status check
        if (connectionStatus === 'accepted') {
            setIsConnected(true);
        }
    }, [id, connectionStatus]);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const response = await api.get(`/user/${id}/posts`);
                console.log('Fetched posts:', response.data); // Inspect the response
                setPosts(response.data);
            } catch (error) {
                console.error('Error fetching user posts:', error);
            }
        };
        fetchPosts();
    }, [id]);

    useEffect(() => {
        if (selectedVideo && videoRef.current && !hasIncrementedView) {
            const videoElement = videoRef.current;

            const handleTimeUpdate = () => {
                if (videoElement.currentTime >= 10 && !hasIncrementedView) {
                    // Call the backend route
                    api.post(`/video-posts/${selectedVideo.id}/view`, {}, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    })
                        .then(() => {
                            setHasIncrementedView(true);
                            console.log('View count incremented successfully'); // Log success
                        })
                        .catch((error) => {
                            console.error('Error incrementing view count:', error);
                        });
                }
            };

            videoElement.addEventListener('timeupdate', handleTimeUpdate);

            // Log when the event listener is added
            console.log('Added timeupdate event listener');

            return () => {
                videoElement.removeEventListener('timeupdate', handleTimeUpdate);
                console.log('Removed timeupdate event listener');
            };
        }
    }, [selectedVideo, hasIncrementedView]);

    const handleConnectionRequest = async () => {
        try {
            await api.post('/user/connections/request', { receiverId: id });
            setConnectionStatus('pending');
        } catch (error) {
            console.error('Error sending connection request:', error);
            alert('Failed to send connection request');
        }
    };

    const handleRemoveConnection = async () => {
        try {
            await api.post('/user/connections/remove', { userId: id });
            setConnectionStatus('none');
        } catch (error) {
            console.error('Error removing connection:', error);
            alert('Failed to remove connection');
        }
    };

    const handleVideoSelect = (video) => {
        setSelectedVideo(video);
        fetchComments(video.id);
    };

    const fetchComments = async (postId) => {
        try {
            const response = await api.get(`/video-posts/${postId}/comments`);
            setComments(response.data);
        } catch (error) {
            console.error('Error fetching comments:', error);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        try {
            const response = await fetch(`/api/videos/${selectedVideo.id}/comments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ comment: newComment })
            });
            if (response.ok) {
                const addedComment = await response.json();
                setComments(prevComments => [addedComment, ...prevComments]);
                setNewComment('');
            } else {
                console.error('Failed to post comment');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };

    const handleCommentDelete = (commentId) => {
        setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
    };

    const handleCommentLike = (commentId, isLiked) => {
        setComments(prevComments => prevComments.map(comment =>
            comment.id === commentId
                ? { ...comment, liked: isLiked, like_count: isLiked ? comment.like_count + 1 : comment.like_count - 1 }
                : comment
        ));
    };

    const sortedPosts = [...posts].sort((a, b) => {
        if (sortBy === 'recent') {
            return new Date(b.created_at) - new Date(a.created_at);
        }
        return b.view_count - a.view_count;
    });

    const isOwnProfile = currentUserId === parseInt(id);

    const canViewVideo = (post) => {
        // Adjust the path to access 'visibility' correctly
        const visibility = post.visibility || post.vp?.visibility; // Modify if 'visibility' is nested
        console.log('Post Visibility:', visibility);
        if (visibility && visibility.toLowerCase() === 'public') return true;
        return isOwnProfile || connectionStatus === 'accepted';
    };

    if (loading) return <Loading />; // Replace loading text with component
    if (error) return <div className="user-profile-error">{error}</div>;
    if (!user) return <div className="user-profile-error">User not found</div>;

    return (
        <div className="user-profile">
            <div className="user-profile-header">
                <img
                    src={user.profile_picture || "https://via.placeholder.com/100"}
                    alt={user.display_name}
                    className="user-profile-picture"
                    onError={(e) => { e.target.src = "https://via.placeholder.com/100"; }}
                />
                <h1 className="user-display-name">{user.display_name}</h1>
                <div className="network-size">
                    <FaUserPlus /> {connectionsCount} Connection{connectionsCount !== 1 ? 's' : ''}
                </div>
                {currentUserId !== user.id && (
                    <>
                        {connectionStatus === 'accepted' ? (
                            <div className="profile-menu" ref={menuRef}>
                                <button className="menu-btn" onClick={() => setShowMenu(!showMenu)}>
                                    <FaEllipsisV />
                                </button>
                                {showMenu && (
                                    <div className="menu-dropdown">
                                        <button
                                            className="menu-item danger"
                                            onClick={() => {
                                                setShowConfirmation(true);
                                                setShowMenu(false);
                                            }}
                                        >
                                            Remove Connection
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                className={`connect-btn ${connectionStatus === 'pending' ? 'pending' : ''}`}
                                onClick={handleConnectionRequest}
                                disabled={connectionStatus === 'pending'}
                            >
                                {connectionStatus === 'pending' && 'Request Sent'}
                                {connectionStatus === 'none' && (
                                    <>
                                        <FaUserPlus /> Connect
                                    </>
                                )}
                            </button>
                        )}

                        {/* Confirmation Dialog */}
                        {showConfirmation && (
                            <div className="confirmation-dialog">
                                <div className="confirmation-content">
                                    <h3>Remove Connection</h3>
                                    <p>Are you sure you want to remove {user.display_name} from your connections?</p>
                                    <div className="confirmation-actions">
                                        <button
                                            className="cancel-btn"
                                            onClick={() => setShowConfirmation(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="confirm-btn"
                                            onClick={() => {
                                                handleRemoveConnection();
                                                setShowConfirmation(false);
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="user-info-sections">
                    <div className="user-section company-info">
                        <h3><FaBuilding /> Company Information</h3>
                        <p className="company-name">{user.company_name || 'Company not specified'}</p>
                        <span className="sector-badge">{user.company_sector || 'Sector not specified'}</span>
                    </div>

                    <div className="user-section websites">
                        <h3><FaGlobe /> Websites</h3>
                        <div className="website-links">
                            {user.personal_website && (
                                <a href={user.personal_website} target="_blank" rel="noopener noreferrer" className="website-link">
                                    <FaGlobe /> Personal Website
                                </a>
                            )}
                            {user.company_website && (
                                <a href={user.company_website} target="_blank" rel="noopener noreferrer" className="website-link">
                                    <FaBuilding /> Company Website
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="user-section social-section">
                        <h3>Social Media</h3>
                        <div className="social-links">
                            {user.linkedin_url && (
                                <a href={user.linkedin_url} target="_blank" rel="noopener noreferrer" title="LinkedIn">
                                    <FaLinkedin />
                                </a>
                            )}
                            {user.facebook_url && (
                                <a href={user.facebook_url} target="_blank" rel="noopener noreferrer" title="Facebook">
                                    <FaFacebook />
                                </a>
                            )}
                            {user.instagram_url && (
                                <a href={user.instagram_url} target="_blank" rel="noopener noreferrer" title="Instagram">
                                    <FaInstagram />
                                </a>
                            )}
                            {user.tiktok_url && (
                                <a href={user.tiktok_url} target="_blank" rel="noopener noreferrer" title="TikTok">
                                    <FaTiktok />
                                </a>
                            )}
                            {user.twitter_url && (
                                <a href={user.twitter_url} target="_blank" rel="noopener noreferrer" title="Twitter">
                                    <FaTwitter />
                                </a>
                            )}
                        </div>
                    </div>

                    {user.cv_url && (
                        <div className="cv-section">
                            <a href={user.cv_url} download className="cv-download">
                                <FaFileDownload /> Download CV
                            </a>
                        </div>
                    )}
                </div>
            </div>

            <div className="user-posts-section">
                <div className="posts-header">
                    <h2><FaVideo /> Posted Videos</h2>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                    >
                        <option value="recent">Most Recent</option>
                        <option value="popular">Most Popular</option>
                    </select>
                </div>

                <div className="user-posts-gallery">
                    {sortedPosts.length > 0 ? (
                        sortedPosts.map((post) => (
                            <div
                                key={post.id}
                                className="user-post-item"
                                onClick={() => canViewVideo(post) && handleVideoSelect(post)}
                            >
                                <div className="thumbnail-container">
                                    {/* Fix: Changed condition to check visibility correctly */}
                                    {canViewVideo(post) ? (
                                        <>
                                            <video
                                                src={post.video_url}
                                                className="user-post-video"
                                            />
                                            <div className="play-overlay">
                                                <BsPlayFill />
                                            </div>
                                            <div className="view-count">
                                                <BsEye /> {post.view_count}
                                            </div>
                                            <div className="post-time">
                                                {formatTimeAgo(post.created_at)}
                                            </div>
                                            {post.visibility === 'private' && (
                                                <div className="visibility-indicator">
                                                    🔒 Private
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="private-overlay">
                                            <div className="private-content">
                                                <span className="lock-icon">🔒</span>
                                                <p>This video is private</p>
                                                {!isOwnProfile && (
                                                    <button
                                                        className="connect-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleConnectionRequest();
                                                        }}
                                                        disabled={connectionStatus === 'pending'}
                                                    >
                                                        {connectionStatus === 'pending' ? (
                                                            'Request Sent'
                                                        ) : (
                                                            <>
                                                                <FaUserPlus /> Connect to View
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className="user-post-description">{post.description}</p>
                            </div>
                        ))
                    ) : (
                        <div className="empty-state">No videos posted yet</div>
                    )}
                </div>
            </div>

            {/* Video Modal */}
            {selectedVideo && (
                <div className="video-modal-overlay" onClick={() => {
                    setSelectedVideo(null);
                    setHasIncrementedView(false); // Reset for next view
                }}>
                    <div className="video-modal" onClick={e => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => {
                            setSelectedVideo(null);
                            setHasIncrementedView(false); // Reset for next view
                        }}>
                            <IoClose />
                        </button>
                        <video
                            src={selectedVideo.video_url}
                            autoPlay
                            className="modal-video"
                            ref={videoRef} // Assign the ref
                        />
                        <div className="video-details">
                            <div className="video-info">
                                <h3>{selectedVideo.description}</h3>
                                <div className="video-stats">
                                    <span><BsEye /> {selectedVideo.view_count} views</span>
                                    <span>{formatTimeAgo(selectedVideo.created_at)}</span>
                                </div>
                            </div>
                            <div className="comment-section">
                                <div className="comment-input-container">
                                    <input
                                        type="text"
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        placeholder="Add a comment..."
                                    />
                                    <button onClick={handleAddComment} disabled={!newComment.trim()}>
                                        Post
                                    </button>
                                </div>
                                <div className="comments-list">
                                    {comments.length > 0 ? (
                                        comments.map(comment => (
                                            <CommentItem
                                                key={comment.id}
                                                comment={comment}
                                                onCommentDelete={handleCommentDelete}
                                                onCommentLike={handleCommentLike}
                                            />
                                        ))
                                    ) : (
                                        <div className="no-comments">
                                            Be the first to comment on this video!
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserProfile;